import "core-js/modules/es.array.includes.js";
import PropTypes from "prop-types";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { keysOf } from '../common';
var gutterSizeToClassNameMap = {
  none: null,
  xs: 'euiFlexGroup--gutterExtraSmall',
  s: 'euiFlexGroup--gutterSmall',
  m: 'euiFlexGroup--gutterMedium',
  l: 'euiFlexGroup--gutterLarge',
  xl: 'euiFlexGroup--gutterExtraLarge'
};
export var GUTTER_SIZES = keysOf(gutterSizeToClassNameMap);
var alignItemsToClassNameMap = {
  stretch: null,
  flexStart: 'euiFlexGroup--alignItemsFlexStart',
  flexEnd: 'euiFlexGroup--alignItemsFlexEnd',
  center: 'euiFlexGroup--alignItemsCenter',
  baseline: 'euiFlexGroup--alignItemsBaseline'
};
export var ALIGN_ITEMS = keysOf(alignItemsToClassNameMap);
var justifyContentToClassNameMap = {
  flexStart: null,
  flexEnd: 'euiFlexGroup--justifyContentFlexEnd',
  center: 'euiFlexGroup--justifyContentCenter',
  spaceBetween: 'euiFlexGroup--justifyContentSpaceBetween',
  spaceAround: 'euiFlexGroup--justifyContentSpaceAround',
  spaceEvenly: 'euiFlexGroup--justifyContentSpaceEvenly'
};
export var JUSTIFY_CONTENTS = keysOf(justifyContentToClassNameMap);
var directionToClassNameMap = {
  row: 'euiFlexGroup--directionRow',
  rowReverse: 'euiFlexGroup--directionRowReverse',
  column: 'euiFlexGroup--directionColumn',
  columnReverse: 'euiFlexGroup--directionColumnReverse'
};
export var DIRECTIONS = keysOf(directionToClassNameMap);

var isValidElement = function isValidElement(component) {
  return ['div', 'span'].includes(component);
};

export var EuiFlexGroup = forwardRef(({
  children,
  className,
  gutterSize = 'l',
  alignItems = 'stretch',
  responsive = true,
  justifyContent = 'flexStart',
  direction = 'row',
  wrap = false,
  component = 'div',
  ...rest
}, ref) => {
  const classes = classNames('euiFlexGroup', gutterSizeToClassNameMap[gutterSize], alignItemsToClassNameMap[alignItems], justifyContentToClassNameMap[justifyContent], directionToClassNameMap[direction], {
    'euiFlexGroup--responsive': responsive,
    'euiFlexGroup--wrap': wrap
  }, className);

  if (!isValidElement(component)) {
    throw new Error(`${component} is not a valid element type. Use \`div\` or \`span\`.`);
  }

  return component === 'span' ? <span className={classes} ref={ref} {...rest}>
        {children}
      </span> : <div className={classes} ref={ref} {...rest}>
        {children}
      </div>;
});
EuiFlexGroup.propTypes = {
  alignItems: PropTypes.oneOf(["stretch", "flexStart", "flexEnd", "center", "baseline"]),
  component: PropTypes.oneOf(["div", "span"]),
  direction: PropTypes.oneOf(["row", "rowReverse", "column", "columnReverse"]),
  gutterSize: PropTypes.oneOf(["none", "xs", "s", "m", "l", "xl"]),
  justifyContent: PropTypes.oneOf(["flexStart", "flexEnd", "center", "spaceBetween", "spaceAround", "spaceEvenly"]),
  responsive: PropTypes.bool,
  wrap: PropTypes.bool,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};
EuiFlexGroup.displayName = 'EuiFlexGroup';

try {
  EuiFlexGroup.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/flex/flex_group.tsx',
    description: '',
    displayName: 'EuiFlexGroup',
    methods: [],
    props: {
      alignItems: {
        defaultValue: {
          value: 'stretch'
        },
        description: '',
        name: 'alignItems',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "baseline" | "stretch" | "flexStart" | "flexEnd"',
          value: [{
            value: '"center"'
          }, {
            value: '"baseline"'
          }, {
            value: '"stretch"'
          }, {
            value: '"flexStart"'
          }, {
            value: '"flexEnd"'
          }]
        }
      },
      component: {
        defaultValue: {
          value: 'div'
        },
        description: '',
        name: 'component',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'FlexGroupComponentType',
          value: [{
            value: '"div"'
          }, {
            value: '"span"'
          }]
        }
      },
      direction: {
        defaultValue: {
          value: 'row'
        },
        description: '',
        name: 'direction',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"row" | "column" | "rowReverse" | "columnReverse"',
          value: [{
            value: '"row"'
          }, {
            value: '"column"'
          }, {
            value: '"rowReverse"'
          }, {
            value: '"columnReverse"'
          }]
        }
      },
      gutterSize: {
        defaultValue: {
          value: 'l'
        },
        description: '',
        name: 'gutterSize',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "xs" | "s" | "m" | "l" | "xl"',
          value: [{
            value: '"none"'
          }, {
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      justifyContent: {
        defaultValue: {
          value: 'flexStart'
        },
        description: '',
        name: 'justifyContent',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "flexStart" | "flexEnd" | "spaceBetween" | "spaceAround" | "spaceEvenly"',
          value: [{
            value: '"center"'
          }, {
            value: '"flexStart"'
          }, {
            value: '"flexEnd"'
          }, {
            value: '"spaceBetween"'
          }, {
            value: '"spaceAround"'
          }, {
            value: '"spaceEvenly"'
          }]
        }
      },
      responsive: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'responsive',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      wrap: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'wrap',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement | HTMLSpanElement>'
        }
      }
    },
    extendedInterfaces: ['EuiFlexGroupProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'Attributes']
  };
} catch (e) {}